import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../services/supabase'
import { useCurrentAdvisor } from '../../hooks/useCurrentAdvisor'
import { InboxNotificationRow } from './InboxNotificationRow'
import { InboxNotificationDetail } from './InboxNotificationDetail'

const TYPE_LABELS = {
  mention: '@Mentions',
  task_reassigned: 'Assigned to Me',
  task_watcher_added: 'Watching',
}

export function InboxNotificationTab({ type, isDarkMode, onNavigate }) {
  const { advisor } = useCurrentAdvisor()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)

  const fetch = useCallback(async (advisorId) => {
    setLoading(true)
    const { data } = await supabase
      .from('sfp_notifications')
      .select('*')
      .eq('advisor_id', advisorId)
      .eq('type', type)
      .order('created_at', { ascending: false })
      .limit(100)
    setNotifications(data ?? [])
    setLoading(false)
  }, [type])

  useEffect(() => {
    if (!advisor?.id) return
    fetch(advisor.id)

    const ch = supabase
      .channel(`inbox-notif-${type}-${advisor.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'sfp_notifications',
        filter: `advisor_id=eq.${advisor.id}`,
      }, (payload) => {
        if (payload.new.type === type) {
          setNotifications(prev => [payload.new, ...prev])
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'sfp_notifications',
        filter: `advisor_id=eq.${advisor.id}`,
      }, (payload) => {
        if (payload.new.type === type) {
          setNotifications(prev => prev.map(n => n.id === payload.new.id ? payload.new : n))
        }
      })
      .subscribe()

    return () => supabase.removeChannel(ch)
  }, [advisor?.id, fetch, type])

  const markAsRead = useCallback(async (notif) => {
    if (notif.is_read) return
    await supabase
      .from('sfp_notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', notif.id)
    setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n))
  }, [])

  const markAllRead = useCallback(async () => {
    if (!advisor?.id) return
    const ids = notifications.filter(n => !n.is_read).map(n => n.id)
    if (!ids.length) return
    await supabase.from('sfp_notifications').update({ is_read: true, read_at: new Date().toISOString() }).in('id', ids)
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
  }, [advisor?.id, notifications])

  const handleSelect = (notif) => {
    setSelected(notif)
    markAsRead(notif)
  }

  const unreadCount = notifications.filter(n => !n.is_read).length

  return (
    <div className="flex h-full">
      {/* Left list */}
      <div className={`w-80 flex-shrink-0 flex flex-col border-r ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
        <div className={`flex items-center justify-between px-4 py-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
          <span className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            {TYPE_LABELS[type]}
          </span>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-sfp-orange hover:underline font-medium"
            >
              Mark all read
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className={`flex items-center justify-center h-32 text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
              Loading…
            </div>
          ) : notifications.length === 0 ? (
            <div className={`flex items-center justify-center h-32 text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
              No notifications
            </div>
          ) : notifications.map(n => (
            <InboxNotificationRow
              key={n.id}
              notification={n}
              isSelected={selected?.id === n.id}
              onClick={() => handleSelect(n)}
              isDarkMode={isDarkMode}
            />
          ))}
        </div>
      </div>

      {/* Right detail */}
      <div className="flex-1 overflow-y-auto">
        {selected ? (
          <InboxNotificationDetail
            notification={selected}
            isDarkMode={isDarkMode}
            onNavigate={onNavigate}
          />
        ) : (
          <div className={`flex items-center justify-center h-full text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
            Select a notification to view details
          </div>
        )}
      </div>
    </div>
  )
}
